import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import { Apple, Dumbbell, HeartPulse, Pill, Smartphone, Send, Check, Loader2 } from "lucide-react";
import { loadAnalysis, loadLeadEmail } from "@/lib/persistence";
import { submitLeadCapture } from "@/lib/sentinelEmailApi";
import { ASSETS } from "@/lib/assets";
import { toast } from "sonner";

const PILLAR_ICONS = [Pill, Apple, Dumbbell, HeartPulse];

function PillarCard({ label, icon: Icon, index, videoSrc }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06 }}
      className="relative min-h-[148px] overflow-hidden rounded-2xl border border-white/10"
      data-testid={`email-pillar-${index}`}
    >
      <img
        src={ASSETS.heroPoster}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={ASSETS.heroPoster}
        className="absolute inset-0 h-full w-full object-cover"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/45 to-zinc-950/30" />
      <div className="relative z-10 flex h-full min-h-[148px] flex-col justify-end gap-2 p-3.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/15 text-white backdrop-blur-sm">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-xs font-semibold leading-tight text-white">{label}</span>
      </div>
    </motion.div>
  );
}

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function EmailCapture({ variant = "section", onSubmitted, compact = false }) {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [wantsApp, setWantsApp] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const lead = loadLeadEmail();
    if (!lead?.email) return;
    setEmail(lead.email);
    if (lead.wantsApp !== undefined) setWantsApp(!!lead.wantsApp);
    if (lead.unsubscribeToken) setSubmitted(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError(t.emailCapture.invalid);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const analysisResult = loadAnalysis()?.result;
      await submitLeadCapture({
        email: email.trim(),
        wantsApp,
        locale: lang,
        result: analysisResult,
      });
      setSubmitted(true);
      toast.success(t.emailCapture.success);
      onSubmitted?.();
    } catch (err) {
      setError(t.emailCapture.networkError);
      toast.error(t.emailCapture.networkError);
    } finally {
      setSubmitting(false);
    }
  };

  const wrapClass =
    variant === "modal"
      ? "p-6"
      : "relative overflow-hidden rounded-[32px] border border-zinc-200 bg-white p-7 md:p-10";

  return (
    <section
      id="recommendations"
      data-testid="email-capture"
      className={variant === "section" ? "container-x scroll-mt-24 py-16 md:py-20" : ""}
    >
      <div className={wrapClass}>
        {variant === "section" && (
          <>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(81,75,186,0.18), transparent)" }}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -left-24 -bottom-24 h-[320px] w-[320px] rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(196,192,245,0.4), transparent)" }}
            />
          </>
        )}

        <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <span className="overline" data-testid="email-capture-overline">
              {t.emailCapture.overline}
            </span>
            <h2 className="mt-4 text-3xl text-zinc-900 md:text-[42px] md:leading-[1.06]">
              {t.emailCapture.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-zinc-600">{t.emailCapture.subtitle}</p>

            {!compact && (
              <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {t.emailCapture.pillars.map((p, i) => (
                  <PillarCard
                    key={i}
                    label={p}
                    icon={PILLAR_ICONS[i] || Pill}
                    index={i}
                    videoSrc={ASSETS.pillarVideos[i] || ASSETS.pillarVideos[0]}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-3xl border-2 border-emerald-200 bg-emerald-50/60 p-7 text-center"
                data-testid="email-capture-success"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Check className="h-6 w-6" strokeWidth={3} />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-emerald-900">
                  {t.emailCapture.successTitle}
                </h3>
                <p className="mt-2 text-sm text-emerald-800">{t.emailCapture.successBody}</p>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(81,75,186,0.45)]"
                data-testid="email-capture-form"
              >
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  {t.emailCapture.emailLabel}
                </label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailCapture.emailPlaceholder}
                    className="flex-1 rounded-full border border-zinc-200 bg-white px-5 py-3 text-sm text-zinc-900 outline-none transition focus:border-[color:var(--sentinel)]"
                    data-testid="email-capture-input"
                    autoComplete="email"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="email-capture-submit"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t.emailCapture.submit}
                  </button>
                </div>
                {error && (
                  <p className="mt-2 text-xs font-medium text-red-600" data-testid="email-capture-error">
                    {error}
                  </p>
                )}

                <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4">
                  <input
                    type="checkbox"
                    checked={wantsApp}
                    onChange={(e) => setWantsApp(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[color:var(--sentinel)]"
                    data-testid="email-capture-app-checkbox"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
                      <Smartphone className="h-3.5 w-3.5 text-[color:var(--sentinel)]" />
                      {t.emailCapture.appOptIn}
                    </span>
                    <span className="mt-0.5 block text-xs text-zinc-500">
                      {t.emailCapture.appOptInSub}
                    </span>
                  </span>
                </label>

                <p className="mt-4 text-[11px] leading-relaxed text-zinc-400">
                  {t.emailCapture.privacy}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
